import PropTypes from "prop-types";
import React, { useState, useMemo, useEffect } from "react";
import {
  Typography,
  Button,
  Box,
  MenuItem,
  Menu,
  IconButton
} from "@mui/material";
import { Add, SaveAlt as ImportIcon } from "@mui/icons-material";
import Modal from "../Modal/Modal";
import { useTranslation } from "react-i18next";
import ButtonGroup from "../Buttons/ButtonGroup";
import UploadQuestionsModal from "./UploadQuestionsModal";
import { stripHtml } from "../../utils/helpers";
import makeStyles from "@mui/styles/makeStyles";
import { ExpandLess, ExpandMore, Delete } from "@mui/icons-material";
import clsx from "clsx";
import { reorder } from "../../utils/tableUtils";
import { sortBy, invert } from "lodash";
import {
  quizQuestionTypes,
  surveyQuestionTypes,
  quizQuestionTypesPresentation
} from "../../constants";
import Table from "../ReactTable/Table";

const useStyles = makeStyles(() => ({
  questionTextCell: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    alignSelf: "center"
  },
  questionTextCellExpanded: {
    whiteSpace: "normal"
  }
}));

const QuestionBankColumn = ({ value }) => (
  <Box height={30} display="flex" alignItems="center">
    {value}
  </Box>
);

/**
 * The cells in the Question Text column can be expanded. If it's not expanded,
 * a preview with just the text from the question (not the HTML) is shown. If the
 * user expands the cell, the full question with the HTML is shown.
 */
const QuestionTextContentColumn = ({ questionText }) => {
  const [expanded, setExpanded] = useState(false);
  const classes = useStyles();

  const previewText = questionText ? stripHtml(questionText, true) : "";

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      justifyContent="space-between"
      maxWidth={570}
    >
      <div
        className={clsx(classes.questionTextCell, {
          [classes.questionTextCellExpanded]: expanded
        })}
        dangerouslySetInnerHTML={{
          __html: expanded ? questionText : previewText
        }}
      ></div>
      <IconButton
        onClick={e => {
          setExpanded(!expanded);
          e.stopPropagation();
        }}
        size="small"
      >
        {expanded ? <ExpandLess /> : <ExpandMore />}
      </IconButton>
    </Box>
  );
};

const questionTextColumn = {
  Header: "Question",
  accessor: "text",
  id: "text",
  Cell: ({ cell: { value } }) => (
    <QuestionTextContentColumn questionText={value} />
  ),
  style: {
    width: "70%"
  }
};

const questionTypeColumn = {
  Header: "Type",
  accessor: "questionType",
  id: "questionType",
  style: {
    width: "10%"
  }
};

const quizQuestionTypeColumn = {
  ...questionTypeColumn,
  Cell: ({ cell: { value } }) => {
    return (
      <QuestionBankColumn
        value={quizQuestionTypesPresentation[invert(quizQuestionTypes)[value]]}
      />
    );
  }
};

const surveyQuestionTypeColumn = {
  ...questionTypeColumn,
  Cell: ({ cell: { value } }) => {
    return (
      <QuestionBankColumn
        value={
          quizQuestionTypesPresentation[invert(surveyQuestionTypes)[value]]
        }
      />
    );
  }
};

const pointsColumn = {
  Header: "Points",
  accessor: "points",
  id: "points",
  Cell: ({ cell: { value } }) => <QuestionBankColumn value={value} />,
  style: {
    width: "10%"
  }
};

const mandatoryColumn = {
  Header: "Mandatory",
  accessor: "mandatory",
  id: "mandatory",
  Cell: ({ cell: { value } }) => (
    <QuestionBankColumn value={value ? "Yes" : "No"} />
  ),
  style: {
    width: "10%"
  }
};

const quizColumns = [
  questionTextColumn,
  quizQuestionTypeColumn,
  pointsColumn,
  mandatoryColumn
];

const surveyColumns = [
  questionTextColumn,
  surveyQuestionTypeColumn,
  mandatoryColumn
];

const AddQuestionDropdown = ({ onSelect, isSurvey, setDisableCansel }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = questionType => {
    onSelect(questionType);
    setDisableCansel(true);
    handleClose();
  };

  const questionTypeMenuItems = isSurvey
    ? Object.values(surveyQuestionTypes)
    : Object.values(quizQuestionTypes);

  return (
    <div>
      <Button
        aria-controls="new-question-menu"
        aria-haspopup="true"
        color="primary"
        variant="contained"
        onClick={handleClick}
        startIcon={<Add />}
      >
        {t("questionBank.addQuestionBtn")}
      </Button>
      <Menu
        id="new-question-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {questionTypeMenuItems.map(questionType => (
          <MenuItem
            onClick={() => handleSelect(questionType)}
            value={questionType}
            key={questionType}
          >
            {t(`questionBank.questionTypes.${questionType}`)}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

const QuestionBank = ({
  onClose,
  open,
  onSubmit,
  questions,
  title,
  type,
  onAddNewQuestion,
  onEditQuestion,
  quizId,
  surveyId,
  onUploadQuestions
}) => {
  const { t } = useTranslation();
  const [updatedQuestions, setUpdatedQuestions] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [uploadQuestionsModalOpen, setUploadQuestionsModalOpen] = useState(
    false
  );
  const [removedItems, setRemovedItems] = useState([]);
  const [disableCansel, setDisableCansel] = useState(false);
  const [currentQuizOrSurveyId, setCurrentQuizOrSurveyId] = useState(
    quizId || surveyId
  );
  useEffect(() => {
    if (questions) {
      // sort the questions by the "order" property
      // check that user has started editing the quiz
      const newQuizOrSurveyId = quizId || surveyId;
      if (newQuizOrSurveyId != currentQuizOrSurveyId) {
        // sort the current questions by the "order" property
        setCurrentQuizOrSurveyId(newQuizOrSurveyId);
        setUpdatedQuestions(sortBy(questions, ["order"]));
      } else {
        //remove questions that user wants to delete and sort the updated questions by saved positions
        let questionsWithoutRemoved = questions;
        if (removedItems.length > 0) {
          questionsWithoutRemoved = questionsWithoutRemoved.filter(
            i => !removedItems.includes(i.id)
          );
        }
        updatedQuestions.map((question, index) => {
          let questionsWithoutRemovedCopy = questionsWithoutRemoved.slice();
          const orderIndex = questionsWithoutRemovedCopy.findIndex(
            obj => obj.id === question.id
          );
          if (orderIndex != -1) {
            const [removed] = questionsWithoutRemoved.splice(orderIndex, 1);
            questionsWithoutRemoved.splice(index, 0, removed);
          }
        });
        setUpdatedQuestions(questionsWithoutRemoved);
      }
    }
  }, [questions]);

  useEffect(() => setDisableCansel(false), [open]);

  const handleSelectedRowsChange = rows => {
    setSelectedRows(rows ? rows.map(id => ({ id: parseInt(id) })) : []);
  };

  const handleDeleteQuestions = () => {
    let selectedIds = selectedRows.map(r => r.id);

    setRemovedItems(selectedIds);
    let remainingQuestions = updatedQuestions.filter(
      i => !selectedIds.includes(i.id)
    );

    setUpdatedQuestions(remainingQuestions);
    setDisableCansel(false);
    setSelectedRows([]);
  };

  const handleReorder = reorderedRows => {
    // dropped outside the list
    if (!reorderedRows.destination) {
      return;
    }
    const rows = reorder(
      updatedQuestions,
      reorderedRows.source.index,
      reorderedRows.destination.index
    );
    setDisableCansel(false);
    setUpdatedQuestions(rows);
  };

  const handleSaveQuestions = () => {
    // update the "order" property with the current order of the quiz questions
    const newQuestions = updatedQuestions.map(
      ({ tableData, ...questionProps }, index) => ({
        ...questionProps,
        order: index,
        courseQuizId: quizId || surveyId
      })
    );
    onSubmit(newQuestions);
  };

  const handleCancel = () => {
    setUpdatedQuestions(questions);
    setDisableCansel(true);
    onClose();
  };

  const memoColumns = useMemo(
    () => (type === "survey" ? surveyColumns : quizColumns),
    [type]
  );

  return (
    <Modal
      disableEnforceFocus
      showCloseIcon
      maxWidth="lg"
      fullWidth
      fillHeight
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {title}
        </Typography>
      }
      dialogContent={
        <Box display="flex" flexDirection="column" my={1} height="100%">
          <Box
            mb={3}
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            flexDirection={{ xs: "column", md: "row" }}
          >
            <Button
              color="primary"
              variant="contained"
              onClick={() => handleDeleteQuestions()}
              startIcon={<Delete />}
              disabled={selectedRows.length === 0}
            >
              {t("questionBank.deleteQuestionsBtn")}
            </Button>
            <Box mt={{ xs: 6, md: 0 }}>
              <ButtonGroup align="right">
                <AddQuestionDropdown
                  onSelect={onAddNewQuestion}
                  isSurvey={!!surveyId}
                  setDisableCansel={setDisableCansel}
                />
                <Button
                  color="primary"
                  variant="contained"
                  data-qa="import-questions-btn"
                  onClick={() => setUploadQuestionsModalOpen(true)}
                  startIcon={<ImportIcon />}
                >
                  {t("questionBank.importQuestionsBtn")}
                </Button>
              </ButtonGroup>
            </Box>
          </Box>
          <Table
            columns={memoColumns}
            onRowClick={rowData => onEditQuestion(rowData.original)}
            onMoveRow={handleReorder}
            updateSelection={handleSelectedRowsChange}
            showSelectAllCheckbox
            data={updatedQuestions}
            manualSortBy={false}
          />
          <UploadQuestionsModal
            open={uploadQuestionsModalOpen}
            onClose={() => setUploadQuestionsModalOpen(false)}
            type={type}
            onUploadQuestions={onUploadQuestions}
          />
        </Box>
      }
      dialogActions={
        <>
          <Button
            color="primary"
            onClick={handleCancel}
            disabled={disableCansel}
          >
            {t("questionBank.cancelBtn")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSaveQuestions()}
          >
            {t(`questionBank.saveQuestionsBtn.${type}`)}
          </Button>
        </>
      }
      open={open}
      onClose={onClose}
    />
  );
};

QuestionBank.propTypes = {
  /**
   * Called to close the Modal
   */
  onClose: PropTypes.func,
  /**
   * True if the modal is open, false otherwise
   */
  open: PropTypes.bool,
  /**
   * Called when the form is submitted to save the quiz questions
   */
  onSubmit: PropTypes.func,
  /**
   * The title of the question bank modal
   */
  title: PropTypes.string,
  /**
   * Questions data
   */
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      questionType: PropTypes.string,
      order: PropTypes.number,
      points: PropTypes.number,
      mandatory: PropTypes.bool
    })
  ),
  type: PropTypes.oneOf(["quiz", "survey"]),
  quizId: PropTypes.string,
  surveyId: PropTypes.string
};

export default QuestionBank;
